import { useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * Portal-rendered modal wrapper. Renders into document.body so the modal
 * always escapes any ancestor stacking context / transform / filter that
 * might trap a normal `position: fixed` element inside the page wrapper.
 *
 * Also: locks <body> scroll while open, closes on Esc, and closes on
 * backdrop click.
 */
export default function Modal({ open, onClose, children, testid = "modal" }) {
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-brand-ink/40 backdrop-blur-sm overflow-y-auto p-4 sm:p-6"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl my-4 sm:my-10"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}
